import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Check, X, Copy } from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import { Toast } from '../Toast';

const PLANS = [
  {
    slug: 'teste',
    name: 'Teste',
    price: 11,
    period: '/teste',
    badge: '🧪 TESTE',
    features: [
      'Plano para validar pagamento',
      'Imagem de pagamento aprovado',
      'Ativação do plano no perfil'
    ]
  },
  {
    slug: 'starter',
    name: 'Starter',
    price: 97,
    period: '/mês',
    badge: null,
    features: [
      'Até 3 Contas Conectadas',
      'Encaminhamento Básico',
      'Substituição de Links',
      'Gateway Nativo Integrado',
      'Suporte Especializado'
    ]
  },
  {
    slug: 'pro',
    name: 'Pro Scale',
    price: 197,
    period: '/mês',
    badge: '⭐ MAIS VENDIDO',
    features: [
      'Contas Ilimitadas',
      'Filtros de Conteúdo Avançados',
      'Sistema Anti-Ban & Delay Pro',
      'Automação de Checkout VIP',
      'Taxas de Gateway Reduzidas',
      'Suporte Prioritário 24/7'
    ]
  },
  {
    slug: 'lifetime',
    name: 'Lifetime',
    price: 697,
    period: '/único',
    badge: null,
    features: [
      'Acesso Vitalício Garantido',
      'Todas as Features Futuras',
      'Sem Mensalidades',
      'Setup Individual Orientado',
      'Acesso ao Grupo de Networking',
      'Gateway Nativo VIP'
    ]
  }
];

function formatCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function isValidCpf(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
  let rest = (sum * 10) % 11;
  if (rest === 10) rest = 0;
  if (rest !== parseInt(d[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10) rest = 0;
  return rest === parseInt(d[10]);
}

function planSlugToDisplayName(slug: string | null): string {
  if (!slug || slug === 'free') return 'Gratuito';
  const plan = PLANS.find((p) => p.slug === slug);
  return plan ? plan.name : slug;
}

export const Assinar: React.FC = () => {
  const { refreshSubscription, planType, hasActiveSubscription } = useAuth();
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [modalPlan, setModalPlan] = useState<typeof PLANS[0] | null>(null);
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [pixId, setPixId] = useState<string | null>(null);
  const [paymentRef, setPaymentRef] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [activeSlugs, setActiveSlugs] = useState<string[] | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    api.get<{ slugs: string[] }>('/assinar/plans').then((r) => setActiveSlugs(r.data?.slugs ?? [])).catch(() => setActiveSlugs([]));
  }, []);

  const plansToShow = activeSlugs === null ? PLANS : PLANS.filter((p) => activeSlugs.includes(p.slug));
  const cpfDigits = cpf.replace(/\D/g, '');

  const handleAssinarClick = (plan: typeof PLANS[0]) => {
    setModalPlan(plan);
    setPixCode(null);
    setPixId(null);
    setPaymentRef(null);
    setPaymentStatus(null);
    setCpf('');
    api.get<{ profile?: { cpf?: string } }>('/me').then((r) => {
      const raw = r.data?.profile?.cpf;
      if (raw && String(raw).replace(/\D/g, '').length === 11) setCpf(formatCpf(String(raw)));
    }).catch(() => {});
  };

  const cpfValid = cpfDigits.length === 11 && isValidCpf(cpfDigits);

  const handlePagarAgora = async () => {
    if (!modalPlan || !cpfValid) {
      setToast({ msg: 'Informe um CPF válido.', type: 'error' });
      return;
    }
    setLoading(true);
    setToast(null);
    try {
      const res = await api.post<{ code: string; pixId: string; paymentRef?: string }>('/assinar/create-pix', {
        planSlug: modalPlan.slug,
        cpf: cpfDigits
      });
      setPixCode(res.data.code);
      setPixId(res.data.pixId);
      if (res.data.paymentRef) setPaymentRef(res.data.paymentRef);
      setPaymentStatus('waiting');
    } catch (e: unknown) {
      const err = e as { response?: { status: number; data?: { error?: string; code?: string } } };
      const code = err.response?.data?.code;
      const msg = err.response?.data?.error || 'Erro ao gerar PIX.';
      if (err.response?.status === 401 && (code === 'TOKEN_MISSING' || msg.includes('Token não fornecido'))) {
        setToast({ msg: 'Sessão expirada. Faça login novamente.', type: 'error' });
        localStorage.removeItem('hotlize_token');
        localStorage.removeItem('hotlize_refresh_token');
        setTimeout(() => window.location.href = '/login', 1500);
      } else {
        setToast({ msg, type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModalAsPaid = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    refreshSubscription().then(() => {
      setToast({ msg: 'Pagamento confirmado! Seu plano foi ativado.', type: 'success' });
      setTimeout(() => {
        setModalPlan(null);
        setPixCode(null);
        setPixId(null);
        setPaymentRef(null);
        setPaymentStatus(null);
      }, 1500);
    });
  }, [refreshSubscription]);

  const applyStatusResult = useCallback((status: string, planJustBought?: string) => {
    setPaymentStatus(status);
    if (status === 'verified') {
      closeModalAsPaid();
      return;
    }
    if (status === 'expired' || status === 'refunded') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setToast({
        msg: status === 'expired' ? 'Cobrança expirada.' : 'Pagamento não confirmado (CPF divergente).',
        type: 'error'
      });
      return;
    }
    if (planJustBought && status === 'pending') {
      refreshSubscription()
        .then(() => api.get<{ profile?: { plan_type?: string } }>('/me'))
        .then((res) => {
          const current = res?.data?.profile?.plan_type ?? null;
          if (current === planJustBought) closeModalAsPaid();
        })
        .catch(() => {});
    }
  }, [closeModalAsPaid, refreshSubscription]);

  const statusId = paymentRef || pixId;

  useEffect(() => {
    if (!statusId || paymentStatus !== 'waiting' || !modalPlan) return;
    const planSlug = modalPlan.slug;
    const poll = async () => {
      try {
        const res = await api.get<{ status: string }>(`/assinar/status/${statusId}`);
        const status = (res.data?.status ?? '').toLowerCase();
        applyStatusResult(status, planSlug);
      } catch {
        // Erro de rede ou 4xx/5xx: manter polling ativo, não alterar paymentStatus
      }
    };
    const initialDelay = setTimeout(() => {
      poll();
      pollRef.current = setInterval(poll, 3000);
    }, 2500);
    return () => {
      clearTimeout(initialDelay);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [statusId, paymentStatus, modalPlan, applyStatusResult]);

  const checkPaymentStatusNow = async () => {
    const canCheck = paymentStatus === 'waiting' || paymentStatus === 'pending';
    if (!statusId || !canCheck || !modalPlan) return;
    setCheckingStatus(true);
      try {
        const res = await api.get<{ status: string }>(`/assinar/status/${statusId}`);
        const status = (res.data?.status ?? '').toLowerCase();
        applyStatusResult(status, modalPlan.slug);
      if (status !== 'verified' && status !== 'expired' && status !== 'refunded') {
        setToast({ msg: 'Pagamento ainda não identificado. Aguarde alguns instantes ou confira se o PIX foi pago.', type: 'error' });
      }
    } catch {
      setToast({ msg: 'Não foi possível verificar. Tente novamente em instantes.', type: 'error' });
    } finally {
      setCheckingStatus(false);
    }
  };

  const copyPixCode = () => {
    if (pixCode) {
      navigator.clipboard.writeText(pixCode);
      setToast({ msg: 'Código PIX copiado.', type: 'success' });
    }
  };

  const currentPlanLabel = hasActiveSubscription && planType && planType !== 'free'
    ? `Seu plano atual: ${planSlugToDisplayName(planType)}`
    : 'Plano atual: Gratuito';

  return (
    <div className="dashboard-view" style={{ padding: '24px' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <h1 className="page-title" style={{ marginBottom: '8px' }}>Planos de Assinatura</h1>
      <p className="text-gray-400 text-sm mb-2">Escolha o plano ideal e pague via PIX.</p>
      <p style={{ color: hasActiveSubscription ? '#22c55e' : '#888', fontSize: '14px', marginBottom: '24px' }}>
        {currentPlanLabel}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', maxWidth: '1100px' }}>
        {plansToShow.map((plan) => (
          <div
            key={plan.slug}
            style={{
              background: '#161618',
              borderRadius: '18px',
              border: plan.badge ? '2px solid rgba(255,106,0,0.5)' : '1px solid #333',
              padding: '24px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {plan.badge && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#ff6a00',
                color: '#000',
                fontSize: '10px',
                fontWeight: 800,
                padding: '6px 14px',
                borderRadius: '10px',
                whiteSpace: 'nowrap'
              }}>
                {plan.badge}
              </div>
            )}
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#fff', margin: 0 }}>{plan.name}</h3>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontSize: '28px', fontWeight: 800, color: '#ff6a00' }}>R$ {plan.price}</span>
              <span style={{ fontSize: '14px', color: '#888' }}>{plan.period}</span>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
              {plan.features.map((f, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#ccc', fontSize: '14px' }}>
                  <Check size={18} style={{ flexShrink: 0, color: '#ff6a00' }} />
                  {f}
                </li>
              ))}
            </ul>
            {hasActiveSubscription ? (
              planType === plan.slug ? (
                <GlassButton disabled style={{ marginTop: 'auto', width: '100%' }}>
                  <><Check size={18} /> Assinado</>
                </GlassButton>
              ) : null
            ) : (
              <GlassButton
                onClick={() => handleAssinarClick(plan)}
                style={{ marginTop: 'auto', width: '100%' }}
              >
                <><CreditCard size={18} /> Assinar Agora</>
              </GlassButton>
            )}
          </div>
        ))}
      </div>

      {modalPlan && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !pixCode) setModalPlan(null);
          }}
        >
          <div
            style={{
              background: '#1a1a1a',
              borderRadius: '20px',
              border: '1px solid #333',
              padding: '28px',
              maxWidth: '420px',
              width: '100%'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#fff', fontSize: '20px' }}>{modalPlan.name}</h2>
              <button
                type="button"
                onClick={() => !pixCode && setModalPlan(null)}
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} />
              </button>
            </div>

            {!pixCode ? (
              <>
                <p style={{ color: '#ff6a00', fontSize: '22px', fontWeight: 800, margin: '0 0 16px' }}>
                  R$ {modalPlan.price} {modalPlan.period}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', color: '#aaa', fontSize: '13px' }}>
                  {modalPlan.features.map((f, i) => (
                    <li key={i} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Check size={14} style={{ color: '#ff6a00' }} /> {f}
                    </li>
                  ))}
                </ul>
                <label style={{ display: 'block', color: '#888', fontSize: '12px', marginBottom: '6px' }}>CPF</label>
                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <input
                    type="text"
                    value={cpf}
                    onChange={(e) => setCpf(formatCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    style={{
                      width: '100%',
                      padding: '12px',
                      paddingRight: '40px',
                      borderRadius: '10px',
                      border: `1px solid ${cpfDigits.length === 11 ? (cpfValid ? '#22c55e' : '#ef4444') : '#333'}`,
                      background: '#0d0d0d',
                      color: '#fff',
                      fontSize: '16px'
                    }}
                  />
                  {cpfDigits.length === 11 && (
                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                      {cpfValid
                        ? <Check size={18} style={{ color: '#22c55e' }} />
                        : <X size={18} style={{ color: '#ef4444' }} />
                      }
                    </div>
                  )}
                  {cpfDigits.length === 11 && !cpfValid && (
                    <p style={{ color: '#ef4444', fontSize: '11px', margin: '4px 0 0' }}>CPF inválido.</p>
                  )}
                </div>
                <GlassButton onClick={handlePagarAgora} style={{ width: '100%' }} disabled={loading || !cpfValid}>
                  {loading ? 'Gerando PIX...' : 'Pagar Agora'}
                </GlassButton>
              </>
            ) : paymentStatus === 'verified' ? (
              <p style={{ color: '#22c55e', fontSize: '13px', margin: 0 }}>Pagamento confirmado!</p>
            ) : (
              <>
                <p style={{ color: '#aaa', fontSize: '13px', marginBottom: '12px' }}>
                  Escaneie o QR Code ou copie o código PIX abaixo.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pixCode)}`}
                    alt="QR Code PIX"
                    style={{ borderRadius: '12px', display: 'block' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    readOnly
                    value={pixCode}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #333',
                      background: '#0d0d0d',
                      color: '#fff',
                      fontSize: '12px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={copyPixCode}
                    style={{
                      padding: '10px 14px',
                      background: '#ff6a00',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <Copy size={18} />
                  </button>
                </div>
                {(paymentStatus === 'waiting' || paymentStatus === 'pending') && (
                  <>
                    <p style={{ color: '#fbbf24', fontSize: '12px', margin: 0 }}>
                      {paymentStatus === 'waiting'
                        ? 'Aguardando confirmação do pagamento...'
                        : 'Pagamento ainda não identificado. Clique em Já paguei para verificar.'}
                    </p>
                    <GlassButton
                      type="button"
                      onClick={checkPaymentStatusNow}
                      disabled={checkingStatus}
                      style={{ marginTop: '12px', width: '100%' }}
                    >
                      {checkingStatus ? 'Verificando...' : 'Já paguei'}
                    </GlassButton>
                  </>
                )}
                {paymentStatus === 'expired' && (
                  <p style={{ color: '#ef4444', fontSize: '13px', margin: 0 }}>Cobrança expirada.</p>
                )}
                {paymentStatus === 'refunded' && (
                  <p style={{ color: '#ef4444', fontSize: '13px', margin: 0 }}>Pagamento não confirmado.</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
